/**
 * Goody Fulfillment Recovery — putting the shopper's store selection back.
 *
 * @theme/goody-fulfillment-gate switches the selection to `shipment` with NO
 * store when an on-Goody (marketplace) product is added: Goody ships nationally
 * and is not fulfilled by a store, so `storeId`, `store`, `deliveryType` and
 * `deliveryDate` are all cleared. That is correct while a Goody line is in the
 * cart.
 *
 * It was also permanent. Nothing put the selection back when the last Goody line
 * left, so a shopper who added a Goody product and then emptied their cart was
 * left persisted at `shipment` + no store — a state the header can't rehydrate
 * (its `#restoreState()` bails on a missing zip) and that the personalization
 * drawer read as "no store can fulfil this product", showing
 * "We don't have a store that has this item" on every normal arrangement.
 *
 * This module owns the other half of that transition:
 *
 *   1. The gate calls `stashPreGoodySelection()` BEFORE it overwrites, parking
 *      the store-backed selection under its own key.
 *   2. @theme/ea-cart-lock reports "the cart holds Goody lines" as a derived
 *      signal, exactly like the two booleans it already maintains. When that
 *      goes true the stash is marked as having actually seen Goody in the cart;
 *      when it goes false again the stash is restored and removed.
 *
 * Why the seen-Goody mark: the gate stashes and switches BEFORE the add is
 * posted, and the switch itself writes cart attributes. That write goes through
 * the cart-lock's fetch hook, which re-reads a cart that does not hold the Goody
 * line yet — a plain "no Goody lines → restore" rule would undo the switch
 * milliseconds after making it. Restoring only after Goody has been observed in
 * the cart closes that window, and persisting the mark means it survives a
 * reload mid-flow.
 *
 * Why a dedicated key rather than a field on `ea-store-availability`: that
 * object is the header's selection contract and every key in it is one the
 * header reads back. Same reasoning as `ea-cart-store-active` and
 * `ea-max-fulfillment-option`.
 *
 * The stash is also the ONLY evidence that a `shipment` + no-store record was
 * forced rather than chosen. A shopper whose zip is shipment-only legitimately
 * sits at `shipment` with no store, and for them "no store ships this product"
 * is the true answer. `isGoodyForcedShipment()` is what lets the drawer tell the
 * two apart instead of guessing from an absent field.
 */

import { FULFILLMENT_TYPES } from '@theme/cart-contract';
import {
  STORAGE_KEY,
  readStoreContext,
  saveStoreContext,
  syncCartAttributes,
  updateStoreTriggers,
  emitStoreChange,
} from '@theme/ea-availability';
import { CART_GOODY_ITEMS_EVENT, isCartGoodyActive, isCartNonEmpty } from '@theme/ea-cart-lock';

/** localStorage key holding the parked pre-Goody selection. */
export const PRE_GOODY_KEY = 'ea-pre-goody-selection';

/**
 * Second change event fired by store-availability's own `#notifySelectionChange`.
 * `emitStoreChange` from @theme/ea-availability only fires
 * `store:availability:updated`, but the fulfillment-details drawer, the catering
 * hero and the catering fulfillment gate all listen for this one instead — so a
 * selection written here has to announce on both or those surfaces silently keep
 * the old fulfillment type.
 */
export const LEGACY_CHANGE_EVENT = 'store-availability:change';

/**
 * @typedef {object} PreGoodyRecord
 * @property {object|null} selection the store-backed selection to put back, or
 *   null when there was nothing restorable to park.
 * @property {boolean} seenGoody whether a Goody line has actually been observed
 *   in the cart since the stash was written.
 * @property {number} savedAt epoch ms, for debugging a stuck record.
 */

/**
 * Is this a selection worth putting back?
 *
 * Two requirements, both load-bearing:
 *
 *   • a zip — the header's `#restoreState()` gates its entire rehydration on
 *     `state.zipCode`, so a zipless record restores as nothing at all;
 *   • delivery or pickup — `shipment` is the state we are recovering FROM. The
 *     gate never reaches the stash from an existing shipment selection (it
 *     returns early), so one here means a malformed record.
 *
 * A store-less delivery/pickup record still counts: a shopper who had entered a
 * zip but not yet chosen a store gets that zip back, and the drawer's own store
 * gate then walks them through choosing one — which is exactly where they were.
 *
 * @param {unknown} state
 * @returns {boolean}
 */
export function isRestorableSelection(state) {
  if (!state || typeof state !== 'object') return false;
  const { zipCode, fulfillmentType } = /** @type {Record<string, unknown>} */ (state);
  if (typeof zipCode !== 'string' || zipCode === '') return false;
  return fulfillmentType === FULFILLMENT_TYPES.delivery || fulfillmentType === FULFILLMENT_TYPES.pickup;
}

/**
 * Reads the parked record.
 * @returns {PreGoodyRecord|null}
 */
function readRecord() {
  try {
    const raw = localStorage.getItem(PRE_GOODY_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return {
      selection: parsed.selection && typeof parsed.selection === 'object' ? parsed.selection : null,
      seenGoody: parsed.seenGoody === true,
      savedAt: Number(parsed.savedAt) || 0,
    };
  } catch (e) {
    return null;
  }
}

/** Writes the parked record. Silently no-ops when storage is unavailable. */
function writeRecord(record) {
  try {
    localStorage.setItem(PRE_GOODY_KEY, JSON.stringify(record));
  } catch (e) {
    // Private mode / storage blocked. Without the stash the shopper keeps
    // today's behaviour: the drawer's store gate sends them to the zip flow.
  }
}

/** Removes the parked record. */
function dropRecord() {
  try {
    localStorage.removeItem(PRE_GOODY_KEY);
  } catch (e) {
    // Nothing to do — a record we can't remove is re-validated on next read.
  }
}

/**
 * Park the current selection before the Goody gate overwrites it.
 *
 * Called by @theme/goody-fulfillment-gate immediately before `switchToShipment`
 * writes. A record is written even when there is nothing restorable to park
 * (`selection: null`) — its PRESENCE is the marker that says a later
 * `shipment` + no-store record was forced by Goody rather than chosen, which is
 * what `isGoodyForcedShipment()` answers for the drawer.
 *
 * Never overwrites an existing record: the value that matters is the state the
 * shopper was in before entering Goody-only fulfillment, not before the most
 * recent of several Goody adds. (The gate returns early on a repeat add, so this
 * is a guard rather than the normal path.)
 *
 * @param {object|null} selection the selection about to be replaced
 */
export function stashPreGoodySelection(selection) {
  if (readRecord()) return;
  writeRecord({
    selection: isRestorableSelection(selection) ? selection : null,
    // Normally false: the gate stashes BEFORE the add, so Goody has not reached
    // the cart yet, and the mark is set when the cart-lock reports it arriving.
    //
    // Not always, though. A Goody cart does not lock the fulfillment TYPE (the
    // lock covers store-bound carts, and a Goody cart is store-less), so a
    // shopper holding one can switch back to delivery from the header and then
    // add a second Goody product. The cart already holds Goody at that point, so
    // the lock's signal never CHANGES and no arrival event is ever fired —
    // leaving the mark false forever and the restore permanently disarmed.
    // Seeded from the current state instead of assumed.
    seenGoody: isCartGoodyActive(),
    savedAt: Date.now(),
  });
}

/**
 * Was the current `shipment` + no-store selection forced by the Goody gate?
 *
 * The discriminator between a corrupt selection and a legitimate one: a shopper
 * in a shipment-only zip sits at `shipment` with no store by choice, and for
 * them a product no store can ship really is unavailable.
 *
 * @returns {boolean}
 */
export function isGoodyForcedShipment() {
  return readRecord() !== null;
}

/**
 * Write a selection everywhere the theme reads one: storage, cart attributes,
 * the header triggers, and both change events.
 *
 * The same five steps `switchToShipment` takes, so the switch and its reversal
 * can't drift. Exported for the gate to use on the way in.
 *
 * Cart attributes are only mirrored when the cart actually holds something. A
 * Goody cart holds Goody lines and nothing else (the conflict modal enforces
 * that), so the cart leaving Goody mode is an EMPTY cart — and attributes on an
 * empty cart carry nothing to checkout, get rebuilt by the next add, and would
 * race the attribute reset that a clear path may still have in flight. Same
 * reasoning as the empty-cart bail in @theme/cart-conflict-modal's
 * `syncCartContext`.
 *
 * @param {object} state
 * @param {{syncCart?: boolean}} [options]
 */
export function applySelection(state, options = {}) {
  const syncCart = options.syncCart ?? true;
  saveStoreContext(state);
  // The max-fulfillment window (`ea-max-fulfillment-option`) is deliberately not
  // touched here: it's derived from the cart, and @theme/cart-conflict-modal
  // recomputes it on the cartUpdate that a clear or add fires.
  if (syncCart) syncCartAttributes(state);
  updateStoreTriggers(state);
  emitStoreChange(state);
  document.dispatchEvent(new CustomEvent(LEGACY_CHANGE_EVENT, { detail: state }));
}

/**
 * Drop the selection entirely and announce it, matching store-availability's own
 * `#clearState`: no record, no store context on the cart, blank triggers, both
 * events fired with null.
 *
 * Used when Goody mode ends and there was nothing restorable to go back to.
 * Leaving the forced `shipment` record standing would keep the shopper in the
 * exact state this module exists to end — the drawer's store gate treats
 * `shipment` as a completed selection — while synthesising a delivery record the
 * shopper never chose would be worse. No selection is the honest answer, and it
 * is the one state the normal zip/store flow is built to start from.
 */
function clearSelection() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    // Storage blocked — the events below still reach live consumers.
  }
  if (isCartNonEmpty()) {
    syncCartAttributes({ storeId: '', fulfillmentType: '', deliveryDate: '', zipCode: '', country: '' });
  }
  updateStoreTriggers(null);
  emitStoreChange(null);
  document.dispatchEvent(new CustomEvent(LEGACY_CHANGE_EVENT, { detail: null }));
}

/**
 * Put the pre-Goody selection back, if there is one to put back.
 *
 * Only acts on a selection the Goody gate actually forced: a record that is no
 * longer `shipment`, or whose zip has changed, was not written by the gate, and a
 * shopper who has since chosen something else must not have it taken away. In
 * both cases the
 * record is dropped — Goody mode is over either way — without touching what the
 * shopper currently has.
 *
 * @returns {'restored'|'cleared'|'kept'|'none'} what happened, for callers that
 *   need to decide whether to re-resolve availability or send the shopper to the
 *   zip flow.
 */
export function restorePreGoodySelection() {
  const record = readRecord();
  if (!record) return 'none';

  const current = readStoreContext();
  const stillForced = String(current?.fulfillmentType || '') === FULFILLMENT_TYPES.shipment;
  // A different zip is a selection the shopper made themselves (the Goody cart
  // locks the zip, so the gate's record never changes it). The parked store
  // serves the old zip, not this one.
  const parkedZip = record.selection?.zipCode;
  const zipMoved = Boolean(current?.zipCode && parkedZip && current.zipCode !== parkedZip);

  // The shopper moved on by themselves — nothing to recover, and overwriting
  // their newer choice would be the same kind of loss this module fixes.
  if (!stillForced || zipMoved) {
    dropRecord();
    return 'kept';
  }

  // Re-validated on the way out, not just on the way in: the record may have
  // been written by an older build, hand-edited, or partially written.
  //
  // The PARKED record wins, not just its fulfillmentType. While the Goody line
  // was in the cart the record was a shipment, and `buildStoreState` nulls the
  // store card, `storeName`, `storeAddress` and rewrites `deliveryMethod` to the
  // shipment label for that type. Merging only `fulfillmentType` back left a
  // delivery/pickup record with no store card and a "Shipment (…)" label — the
  // stash is the only place the shopper's store survives. `current` underneath
  // keeps any key the parked record doesn't carry.
  if (isRestorableSelection(record.selection)) {
    const restored = { ...current, ...record.selection };
    applySelection(restored, { syncCart: isCartNonEmpty() });
    dropRecord();
    return 'restored';
  }

  clearSelection();
  dropRecord();
  return 'cleared';
}

/**
 * React to the cart's Goody signal.
 *
 * `true` marks the stash as having seen Goody land — that mark is what makes a
 * later `false` a real exit rather than the gap between stashing and the add.
 * `false` completes the transition.
 *
 * @param {boolean} active
 */
function onGoodySignal(active) {
  const record = readRecord();
  if (!record) return;

  if (active) {
    if (!record.seenGoody) writeRecord({ ...record, seenGoody: true });
    return;
  }

  // Goody was never observed in the cart, so this `false` is either the window
  // between the stash and the add, or an add the shopper abandoned. Leave the
  // record alone: the drawer's store gate recovers the abandoned case on the
  // next add, and the add-in-flight case resolves itself when Goody lands.
  if (!record.seenGoody) return;

  restorePreGoodySelection();
}

document.addEventListener(CART_GOODY_ITEMS_EVENT, (event) => {
  onGoodySignal(event.detail?.active === true);
});

// The cart-lock seeds its signals during its own module evaluation, which runs
// before this module's listener exists (module scripts execute in order). Read
// the settled value directly so a page that LOADS with Goody already gone still
// completes the transition.
onGoodySignal(isCartGoodyActive());
